import React from 'react';
import { useSelector } from 'react-redux';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  @keyframes spinA {
    to { transform: rotate(360deg); }
  }
  @keyframes spinB {
    to { transform: rotate(-360deg); }
  }
  @keyframes spinC {
    to { transform: rotate(360deg); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ripple {
    0%   { transform: scale(1);   opacity: 0.12; }
    100% { transform: scale(2.8); opacity: 0; }
  }
  @keyframes floatY {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-7px); }
  }
  @keyframes dotPulse {
    0%, 80%, 100% { transform: scale(0.7); opacity: 0.3; }
    40%           { transform: scale(1);   opacity: 1; }
  }
  @keyframes shimBrand {
    0%   { background-position: -300% center; }
    100% { background-position:  300% center; }
  }
  @keyframes tagFade {
    from { opacity: 0; letter-spacing: 0.35em; }
    to   { opacity: 1; letter-spacing: 0.22em; }
  }

  .al-root {
    position: fixed;
    inset: 0;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: 'Plus Jakarta Sans', sans-serif;
    z-index: 9999;
    overflow: hidden;
    user-select: none;
  }

  .al-bg {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, #e0e7ff 1.2px, transparent 1.2px);
    background-size: 32px 32px;
    mask-image: radial-gradient(ellipse 65% 65% at 50% 50%, black 10%, transparent 100%);
    opacity: 0.5;
  }

  .al-scene {
    position: relative;
    width: 120px;
    height: 120px;
    margin-bottom: 32px;
    animation: floatY 3.2s ease-in-out infinite;
  }

  .al-ripple {
    position: absolute;
    top: 50%; left: 50%;
    width: 60px; height: 60px;
    margin: -30px 0 0 -30px;
    border-radius: 50%;
    background: #eef2ff;
    animation: ripple 2.8s ease-out infinite;
  }
  .al-ripple.r2 { animation-delay: 0.93s; }
  .al-ripple.r3 { animation-delay: 1.86s; }

  .al-ring {
    position: absolute;
    border-radius: 50%;
    border: 1.5px solid transparent;
  }

  .al-ring.outer {
    inset: 0;
    border-top-color: #6366f1;
    border-right-color: #a5b4fc;
    animation: spinA 2s linear infinite;
  }

  .al-ring.mid {
    inset: 10px;
    border-bottom-color: #818cf8;
    border-left-color: #c7d2fe;
    animation: spinB 1.6s linear infinite;
  }

  .al-ring.inner {
    inset: 22px;
    border-top-color: #6366f1;
    border-right-color: #e0e7ff;
    animation: spinC 2.6s linear infinite;
  }

  .al-core {
    position: absolute;
    top: 50%; left: 50%;
    width: 36px; height: 36px;
    margin: -18px 0 0 -18px;
    border-radius: 50%;
    background: #6366f1;
    box-shadow:
      0 0 0 5px #eef2ff,
      0 0 20px rgba(99,102,241,0.25);
    display: flex; align-items: center; justify-content: center;
  }

  .al-core svg { display: block; }

  .al-brand {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.5px;
    background: linear-gradient(120deg, #312e81 0%, #6366f1 40%, #a5b4fc 60%, #6366f1 80%, #312e81 100%);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimBrand 3.5s linear infinite, fadeUp 0.6s 0.15s ease both;
    opacity: 0;
  }

  .al-tagline {
    font-size: 10px;
    font-weight: 500;
    color: #a5b4fc;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    margin-top: 5px;
    animation: tagFade 0.8s 0.4s ease both, fadeUp 0.6s 0.4s ease both;
    opacity: 0;
  }

  .al-dots {
    display: flex;
    gap: 5px;
    margin-top: 24px;
    animation: fadeUp 0.6s 0.6s ease both;
    opacity: 0;
  }

  .al-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #c7d2fe;
    animation: dotPulse 1.4s ease-in-out infinite;
  }
  .al-dot:nth-child(2) { animation-delay: 0.18s; }
  .al-dot:nth-child(3) { animation-delay: 0.36s; }
`;

export default function FullOverlayLoader() {
  const { activeApp } = useSelector((state) => state.auth);
  const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
  const isBootstrapped = !!bootstrappedApp;

  const appName = isBootstrapped ? activeApp?.name?.toUpperCase() || '' : 'ALISBUILD';

  return (
    <>
      <style>{css}</style>
      <div className="al-root" role="status" aria-label={`Loading ${appName}`}>
        <div className="al-bg" aria-hidden="true" />

        <div className="al-scene" aria-hidden="true">
          <div className="al-ripple" />
          <div className="al-ripple r2" />
          <div className="al-ripple r3" />

          <div className="al-ring outer" />
          <div className="al-ring mid" />
          <div className="al-ring inner" />

          <div className="al-core">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1.5" fill="white" opacity="0.95" />
              <rect x="9" y="2" width="5" height="5" rx="1.5" fill="white" opacity="0.6" />
              <rect x="2" y="9" width="5" height="5" rx="1.5" fill="white" opacity="0.6" />
              <rect x="9" y="9" width="5" height="5" rx="1.5" fill="white" opacity="0.95" />
            </svg>
          </div>
        </div>

        <div className="al-brand">{appName}</div>
        <div className="al-tagline">Loading</div>

        <div className="al-dots" aria-hidden="true">
          <div className="al-dot" />
          <div className="al-dot" />
          <div className="al-dot" />
        </div>
      </div>
    </>
  );
}
